import React, { useState, useCallback } from "react";
import { Box, Button, TextField, useTheme } from "@mui/material";
import { useCreateTrackComment } from "./hooks/use-create-track-comment.hook";

interface AddDeveloperTrackCommentProps {
  trackUId: string;
}

const AddDeveloperTrackComment: React.FC<AddDeveloperTrackCommentProps> = ({
  trackUId,
}) => {
  const theme = useTheme();
  const [newComment, setNewComment] = useState("");

  const createCommentMutation = useCreateTrackComment({
    trackUId: trackUId,
    onSuccess: () => {
      setNewComment("");
    },
  });

  const handleAddComment = useCallback(() => {
    if (newComment.trim() && !createCommentMutation.isPending) {
      createCommentMutation.mutate({
        message: newComment.trim(),
      });
    }
  }, [newComment, createCommentMutation]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleAddComment();
      }
    },
    [handleAddComment],
  );

  return (
    <Box
      sx={{
        padding: theme.nawy.spacing.md,
        borderTop: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: theme.nawy.spacing.sm,
          alignItems: "flex-end",
        }}
      >
        <TextField
          fullWidth
          placeholder="Add new comment"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="small"
          disabled={createCommentMutation.isPending}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: theme.nawy.radius.lg,
              backgroundColor: theme.nawy.colors.semantic.layer.secondary,
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: theme.nawy.colors.semantic.border.primary,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.nawy.colors.semantic.border.accent,
              },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleAddComment}
          disabled={!newComment.trim() || createCommentMutation.isPending}
          sx={{
            backgroundColor: theme.nawy.colors.semantic.layer.accent,
            color: theme.nawy.colors.semantic.text.onColor,
            borderRadius: theme.nawy.radius.lg,
            textTransform: "none",
            padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.md}`,
            boxShadow: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.accentDark,
              boxShadow: "none",
            },
            "&:disabled": {
              backgroundColor: theme.nawy.colors.semantic.layer.tertiary,
              color: theme.nawy.colors.semantic.text.onColor,
            },
          }}
        >
          {createCommentMutation.isPending ? "Adding..." : "Add comment"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddDeveloperTrackComment;
